import React from "react";
import { Navigate, useLocation } from "../hooks/useRouter";
import { tokenManager } from "../utils/utility";
import useIsMobile from "../hooks/useIsMobile";
import { useAuth } from "../hooks/useAuth";
import { useRbac } from "../hooks/useRbac";
import { useRbacInit } from "../hooks/useRbacInit";

import { REQUIRE_AUTH } from "../constant/globalConstants";

export const isAuthenticated = () => {
  if (!REQUIRE_AUTH) return true;
  return !!tokenManager.getToken("token");
};

const ProtectedRoute = ({ element: Component, ...rest }) => {
  const location = useLocation();

  if (!isAuthenticated()) {
    // Store the intended route in sessionStorage before redirecting to login
    if (location.pathname !== "/sign-in") {
      sessionStorage.setItem("intendedRoute", location.pathname);
    }
    return <Navigate to="/sign-in" />;
  }

  return <Component {...rest} />;
};



// Generic route guard that can restrict access based on device/platform or role
const RestrictedRoute = ({
  element: Component,
  restrictMobile = false,
  restrictDesktop = false,
  allowedRoles = null, // Array of allowed roles, e.g., [ROLE_ACCESS.SUPERADMIN]
  allowedAppRoles = null, // Array of allowed app roles, e.g., ['Super Admin']
  appName = null,
  isPublic = false,
  redirectTo = "/chat",
  ...rest
}) => {
  const isMobile = useIsMobile();
  const location = useLocation();
  const { role } = useAuth();
  const { isRbacLoaded } = useRbacInit({ autoFetch: false });
  const { renderAccessDenied, getUserRole, getAppData } = useRbac();

  // Check if access should be restricted based on device type
  const shouldRestrictDevice =
    (restrictMobile && isMobile) || (restrictDesktop && !isMobile);

  // Check if access should be restricted based on role
  const shouldRestrictRole = allowedRoles && !allowedRoles.includes(role);

  if (REQUIRE_AUTH && (shouldRestrictDevice || shouldRestrictRole)) {
    return <Navigate to={redirectTo} replace />;
  }

  if (!isAuthenticated()) {
    // Store the intended route in sessionStorage before redirecting to login
    if (location.pathname !== "/sign-in") {
      sessionStorage.setItem("intendedRoute", location.pathname);
    }
    return <Navigate to="/sign-in" />;
  }

  if (appName && REQUIRE_AUTH && !isPublic) {
    if (!isRbacLoaded) {
      return (
        <div className="chat-loading-screen-section loader-section">
          <div className="loader-inner-section">
            <div className="spinner-small"></div>
          </div>
        </div>
      );
    }

    const accessDeniedElement = renderAccessDenied(appName);
    if (accessDeniedElement) return accessDeniedElement;

    if (allowedAppRoles) {
      const appRole = getUserRole(appName);
      const appData = getAppData(appName);
      const actualRoleName = appData?.role_name;

      const normalizeRole = (r) => (r ? r.toUpperCase().replace(/\s+/g, "") : "");

      const normalizedAllowedRoles = allowedAppRoles.map(normalizeRole);
      const isAllowed =
        normalizedAllowedRoles.includes(normalizeRole(appRole)) ||
        normalizedAllowedRoles.includes(normalizeRole(actualRoleName));

      if (!isAllowed) {
        return <Navigate to={redirectTo} replace />;
      }
    }
  }

  return <Component {...rest} />;
};

// Component to handle default landing redirect based on first available app
const LandingRedirect = () => {
  const { getFirstAccessibleRoute } = useRbac();
  const { isRbacLoaded } = useRbacInit({ autoFetch: false });

  if (!isRbacLoaded) {
    return (
      <div className="chat-loading-screen-section loader-section">
        <div className="loader-inner-section">
          <div className="spinner-small"></div>
        </div>
      </div>
    );
  }

  return <Navigate to={getFirstAccessibleRoute()} replace />;
};

export default ProtectedRoute;
export { RestrictedRoute, LandingRedirect };
